import React, { useEffect, useState, useCallback } from "react";
import { useOutletContext, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { compactNaira, naira, fmtDate } from "../../lib/format";
import { HC } from "../hr/theme";
import { Card, SectionHead, Btn, Empty, Table } from "../hr/ui";
import { KpiCard, Sev, Spinner, Delta } from "./mui";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import {
  TrendingUp, Percent, Boxes, ClipboardCheck, Users, AlertTriangle, Wrench,
  MessageSquareWarning, Sparkles, RefreshCw, Code2, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import RoleDashboard from "../../components/RoleDashboard";

const LABEL_TONE = {
  FACT: [HC.blue, HC.blueTint], OBSERVATION: [HC.purple, HC.purpleTint],
  PREDICTION: [HC.amber, HC.amberTint], RECOMMENDATION: [HC.green, HC.greenTint],
};
const KIND_TONE = {
  risk: [HC.red, HC.redTint], watch: [HC.amber, HC.amberTint],
  opportunity: [HC.purple, HC.purpleTint], positive: [HC.green, HC.greenTint],
};

const FULL_VIEW_ROLES = ["General Manager", "Board/MD", "IT"];

export default function ManagementDashboard() {
  const { user } = useAuth();
  if (user && !user.is_admin && !FULL_VIEW_ROLES.includes(user.role_name)) {
    return <RoleDashboard domain="ops,shift" heading="Operations Dashboard" />;
  }
  return <ManagementDashboardFull />;
}

function ManagementDashboardFull() {
  const { locationId, period } = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [intel, setIntel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [payload, setPayload] = useState(null);
  const [drill, setDrill] = useState(null);

  const load = useCallback(() => {
    const p = { period, location_id: locationId || undefined };
    api.get("/management/dashboard", { params: p }).then(setData).catch((e) => toast.error(formatApiError(e)));
    api.get("/management/intelligence", { params: p }).then(setIntel).catch(() => {});
  }, [period, locationId]);

  useEffect(() => { setData(null); setIntel(null); load(); }, [load]);

  const refreshIntel = async () => {
    setBusy(true);
    try {
      await api.get("/management/intelligence", { params: { period, location_id: locationId || undefined, refresh: 1 } });
      toast.success("Regenerating the management brief — this takes a few seconds");
      setTimeout(() => api.get("/management/intelligence", { params: { period, location_id: locationId || undefined } })
        .then(setIntel).catch(() => {}), 9000);
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const showPayload = async () => {
    try {
      setPayload(await api.get("/management/intelligence/payload", { params: { period, location_id: locationId || undefined } }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openDrill = async (key) => {
    setDrill({ key, loading: true });
    try {
      const d = await api.get(`/management/drilldown/${key}`, { params: { period, location_id: locationId || undefined } });
      setDrill({ key, ...d });
    } catch (e) { toast.error(formatApiError(e)); setDrill(null); }
  };

  if (!data) return <Spinner text="Building the management control centre…" />;

  const k = data.kpis;
  const out = intel?.output || {};

  return (
    <div className="space-y-5" data-testid="mgmt-dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 gap-3">
        <KpiCard testid="kpi-revenue" label="Revenue" value={compactNaira(k.sales.revenue.value)}
          sub={`${k.sales.orders.value} orders · AOV ${compactNaira(k.sales.aov.value)}`}
          delta={k.sales.revenue.change_pct} Icon={TrendingUp} accent={HC.burgundy}
          onClick={() => openDrill("revenue")} />
        <KpiCard testid="kpi-gross-profit" label="Gross Profit" value={compactNaira(k.finance.gross_profit.value)}
          sub={`COGS ${compactNaira(k.finance.cogs.value)}`} delta={k.finance.gross_profit.change_pct}
          Icon={Wallet} accent={HC.green} onClick={() => openDrill("gross_profit")} />
        <KpiCard testid="kpi-food-cost" label="Food Cost %" value={`${k.finance.food_cost_pct.value}%`}
          sub={`${k.finance.food_cost_pct.change_pp >= 0 ? "+" : ""}${k.finance.food_cost_pct.change_pp} pp movement`}
          delta={k.finance.food_cost_pct.change_pct} invert Icon={Percent} accent={HC.amber}
          onClick={() => openDrill("food_cost_pct")} />
        <KpiCard testid="kpi-inventory" label="Inventory Exceptions"
          value={k.inventory.low_stock_items.value + k.inventory.critical_items.value}
          sub={`${k.inventory.critical_items.value} critical · value ${compactNaira(k.inventory.inventory_value.value)}`}
          Icon={Boxes} accent={HC.blue} onClick={() => openDrill("low_stock_items")} />
        <KpiCard testid="kpi-task-completion" label="Task Completion"
          value={`${data.task_completion.completion_pct}%`}
          sub={`${data.task_completion.counts.completed}/${data.task_completion.total} due today`}
          Icon={ClipboardCheck} accent={HC.purple} onClick={() => openDrill("open_tasks")} />
        <KpiCard testid="kpi-missed-tasks" label="Missed Tasks" value={k.operations.missed_tasks.value}
          sub={`${k.operations.open_tasks.value} still open`} Icon={AlertTriangle} accent={HC.red}
          onClick={() => openDrill("missed_tasks")} />
        <KpiCard testid="kpi-workforce" label="Workforce Today" value={k.workforce.present_today.value}
          sub={`${k.workforce.late_today.value} late · ${k.workforce.absent_today.value} absent · ${k.workforce.overtime_hours.value}h OT`}
          Icon={Users} accent={HC.gold} onClick={() => openDrill("present_today")} />
        <KpiCard testid="kpi-ops-load" label="Open Ops Items"
          value={k.operations.open_maintenance.value + k.operations.open_complaints.value}
          sub={`${k.operations.open_maintenance.value} maintenance · ${k.operations.open_complaints.value} complaints`}
          Icon={Wrench} accent={HC.muted} onClick={() => navigate("/management/maintenance")} />
      </div>

      <Card testid="mgmt-ai-brief" className="relative overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1" style={{ background: `linear-gradient(90deg, ${HC.burgundy}, ${HC.gold})` }} />
        <SectionHead
          title={<span className="flex items-center gap-2"><Sparkles size={17} style={{ color: HC.gold }} /> Sir Monty Management Brief</span>}
          sub={intel ? `${intel.source === "ai" ? intel.model : "Deterministic brief"} · generated ${fmtDate(intel.generated_at)} · advisory only, AI cannot act` : "Loading…"}
          right={
            <div className="flex gap-2">
              <Btn variant="outline" onClick={showPayload} data-testid="mgmt-show-payload">
                <Code2 size={15} /> Show me the numbers
              </Btn>
              <Btn variant="primary" onClick={refreshIntel} disabled={busy} data-testid="mgmt-refresh-intel">
                <RefreshCw size={15} className={busy ? "animate-spin" : ""} /> Refresh
              </Btn>
            </div>
          } />
        {!intel ? <Empty text="Generating brief…" /> : (
          <div className="space-y-4">
            {intel.ai_pending && (
              <div className="text-[11px] font-semibold px-3 py-2 rounded-xl"
                style={{ background: HC.amberTint, color: HC.amber }} data-testid="mgmt-ai-pending">
                Showing the deterministic brief while Claude composes the narrative. Hit Refresh in a few seconds.
              </div>
            )}
            <div className="font-brand font-bold text-lg md:text-xl leading-snug" style={{ color: HC.burgundy }} data-testid="mgmt-ai-headline">
              {out.headline}
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {[["What happened", out.what_happened], ["Why it matters", out.why_it_matters],
                ["What's likely next", out.whats_likely_next, true]].map(([t, body, pred]) => (
                <div key={t} className="rounded-xl p-3.5 border" style={{ borderColor: HC.border, background: HC.panelSoft }}>
                  <div className="text-[10px] font-bold tracking-[0.14em] uppercase mb-1.5 flex items-center gap-2" style={{ color: HC.soft }}>
                    {t}
                    {pred && <span className="px-1.5 py-0.5 rounded-full text-[9px]" style={{ background: HC.amberTint, color: HC.amber }}>PREDICTION</span>}
                  </div>
                  <div className="text-sm leading-relaxed" style={{ color: HC.text }}>{body || "—"}</div>
                </div>
              ))}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl p-3.5 border" style={{ borderColor: HC.border }}>
                <div className="text-[10px] font-bold tracking-[0.14em] uppercase mb-2" style={{ color: HC.soft }}>What needs attention</div>
                <ul className="space-y-1.5">
                  {(out.what_needs_attention || []).map((x, i) => (
                    <li key={i} className="text-sm flex gap-2" style={{ color: HC.text }}>
                      <span className="font-bold" style={{ color: HC.burgundy }}>{i + 1}.</span>{x}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl p-3.5 border" style={{ borderColor: HC.greenTint, background: HC.greenTint }}>
                <div className="text-[10px] font-bold tracking-[0.14em] uppercase mb-2" style={{ color: HC.green }}>Recommended action</div>
                <div className="text-sm leading-relaxed" style={{ color: HC.text }} data-testid="mgmt-ai-action">{out.recommended_action || "—"}</div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="mgmt-ai-insights">
              {(out.insights || []).map((ins, i) => {
                const [c, bg] = KIND_TONE[ins.kind] || [HC.muted, HC.borderSoft];
                return (
                  <div key={i} className="rounded-xl border p-3.5 bg-white" style={{ borderColor: HC.border }} data-testid={`mgmt-insight-${i}`}>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="text-sm font-bold" style={{ color: HC.text }}>{ins.title}</span>
                      <span className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase" style={{ background: bg, color: c }}>{ins.kind}</span>
                    </div>
                    <div className="space-y-1.5">
                      {(ins.statements || []).map((s, j) => {
                        const [sc, sbg] = LABEL_TONE[s.label] || [HC.muted, HC.borderSoft];
                        return (
                          <div key={j} className="text-[13px] leading-snug" style={{ color: HC.text }}>
                            <span className="mr-1.5 text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: sbg, color: sc }}>{s.label}</span>
                            {s.text}
                          </div>
                        );
                      })}
                    </div>
                    {!!(ins.metrics || []).length && (
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {ins.metrics.map((m, j) => (
                          <span key={j} className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ background: HC.borderSoft, color: HC.muted }}>{m}</span>
                        ))}
                      </div>
                    )}
                    {ins.supporting_kpi && (
                      <button className="mt-2.5 text-[11px] font-bold" style={{ color: HC.burgundy }}
                        onClick={() => openDrill(ins.supporting_kpi)} data-testid={`mgmt-insight-drill-${i}`}>
                        View supporting data →
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {!!(out.cross_module_correlations || []).length && (
              <div className="rounded-xl border p-3.5" style={{ borderColor: HC.border, background: HC.panelSoft }}>
                <div className="text-[10px] font-bold tracking-[0.14em] uppercase mb-2" style={{ color: HC.soft }}>Cross-module correlations</div>
                {out.cross_module_correlations.map((c, i) => (
                  <div key={i} className="text-[13px] mb-1" style={{ color: HC.text }}>
                    <span className="font-semibold">{(c.modules || []).join(" ↔ ")}:</span> {c.observation}
                    <span className="ml-1.5 text-[10px] font-bold uppercase" style={{ color: HC.muted }}>({c.confidence})</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      <div className="grid xl:grid-cols-3 gap-5">
        <Card testid="mgmt-revenue-trend" className="xl:col-span-2">
          <SectionHead title="Revenue Trend" sub={`Last 30 days · total ${naira(data.revenue_trend.total)}`}
            right={<Delta pct={data.revenue_trend.change_pct} />} />
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.revenue_trend.points}>
                <defs>
                  <linearGradient id="mgmtRev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={HC.burgundy} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={HC.burgundy} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={HC.borderSoft} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: HC.soft }} interval="preserveStartEnd" />
                <YAxis tick={{ fontSize: 11, fill: HC.soft }} tickFormatter={compactNaira} width={64} />
                <Tooltip formatter={(v) => naira(v)} />
                <Area type="monotone" dataKey="revenue" stroke={HC.burgundy} strokeWidth={2} fill="url(#mgmtRev)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card testid="mgmt-alerts-panel">
          <SectionHead title="Alert Centre" sub={`${data.alerts.total} open exceptions`}
            right={<Btn variant="outline" onClick={() => navigate("/management/alerts")} data-testid="mgmt-view-all-alerts">View all</Btn>} />
          <div className="grid grid-cols-4 gap-2 mb-3">
            {["critical", "high", "medium", "low"].map((s) => (
              <div key={s} className="rounded-xl border p-2 text-center" style={{ borderColor: HC.border }}>
                <div className="font-brand font-bold text-lg" style={{ color: HC.text }}>{data.alerts.counts[s] || 0}</div>
                <div className="text-[9px] font-bold uppercase" style={{ color: HC.soft }}>{s}</div>
              </div>
            ))}
          </div>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {data.alerts.alerts.length ? data.alerts.alerts.map((a) => (
              <div key={a.id} className="rounded-xl border p-2.5" style={{ borderColor: HC.border }} data-testid={`mgmt-alert-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="text-[13px] font-semibold leading-snug" style={{ color: HC.text }}>{a.title}</span>
                  <Sev value={a.severity} />
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: HC.soft }}>{a.source_module} · {a.location_name}</div>
              </div>
            )) : <Empty text="No open alerts" />}
          </div>
        </Card>
      </div>

      <div className="grid xl:grid-cols-2 gap-5">
        <Card testid="mgmt-location-league">
          <SectionHead title="Location League Table" sub="Revenue, margin and operational load per outlet" />
          <Table testid="mgmt-location-table" columns={[
            { key: "location_name", label: "Outlet" },
            { key: "revenue", label: "Revenue", align: "right", render: (r) => compactNaira(r.revenue) },
            { key: "change_pct", label: "Δ", align: "right", render: (r) => <Delta pct={r.change_pct} /> },
            { key: "food_cost_pct", label: "Food Cost", align: "right", render: (r) => `${r.food_cost_pct}%` },
            { key: "missed_tasks", label: "Missed", align: "right" },
            { key: "inventory_alerts", label: "Stock", align: "right" },
          ]} rows={data.location_performance} empty="No location data" />
        </Card>

        <Card testid="mgmt-top-products">
          <SectionHead title="Top Products" sub="Highest revenue contributors this period" />
          <Table testid="mgmt-products-table" columns={[
            { key: "name", label: "Product" },
            { key: "quantity", label: "Qty", align: "right" },
            { key: "revenue", label: "Revenue", align: "right", render: (r) => compactNaira(r.revenue) },
            { key: "change_pct", label: "Δ", align: "right", render: (r) => <Delta pct={r.change_pct} /> },
          ]} rows={data.top_products} empty="No sales in this period" />
        </Card>
      </div>

      <div className="grid xl:grid-cols-3 gap-5">
        <Card testid="mgmt-maintenance-due">
          <SectionHead title="Maintenance Due" sub="Upcoming service schedules" />
          {data.maintenance_due.length ? data.maintenance_due.map((m) => (
            <div key={m.id} className="flex items-center justify-between gap-2 py-2 border-t first:border-0" style={{ borderColor: HC.borderSoft }}>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold truncate" style={{ color: HC.text }}>{m.equipment}</div>
                <div className="text-[11px]" style={{ color: HC.soft }}>{m.location_name} · {fmtDate(m.next_due_at)}</div>
              </div>
              <Sev value={m.priority} />
            </div>
          )) : <Empty text="Nothing due" />}
        </Card>
        <Card testid="mgmt-renewals">
          <SectionHead title="Renewals" sub="Licences, contracts and subscriptions" />
          {data.renewals.length ? data.renewals.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-2 py-2 border-t first:border-0" style={{ borderColor: HC.borderSoft }}>
              <div className="min-w-0">
                <div className="text-[13px] font-semibold truncate" style={{ color: HC.text }}>{r.service_name}</div>
                <div className="text-[11px]" style={{ color: HC.soft }}>{r.vendor || "—"} · in {r.days} day(s)</div>
              </div>
              <Sev value={r.priority} />
            </div>
          )) : <Empty text="No upcoming renewals" />}
        </Card>
        <Card testid="mgmt-complaints-panel">
          <SectionHead title="Latest Complaints" sub="Customer and staff"
            right={<Btn variant="outline" onClick={() => navigate("/management/complaints")}>Open</Btn>} />
          {data.complaints.length ? data.complaints.map((c) => (
            <div key={c.id} className="py-2 border-t first:border-0" style={{ borderColor: HC.borderSoft }}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[13px] font-semibold" style={{ color: HC.text }}>{c.reference} · {c.category}</span>
                <Sev value={c.priority} />
              </div>
              <div className="text-[11px] truncate" style={{ color: HC.soft }}>{c.location_name} · {c.description}</div>
            </div>
          )) : <Empty text="No complaints logged" />}
        </Card>
      </div>

      {payload && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setPayload(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()} data-testid="mgmt-payload-modal">
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: HC.border }}>
              <div>
                <div className="font-brand font-bold text-lg" style={{ color: HC.text }}>Deterministic AI payload</div>
                <div className="text-xs" style={{ color: HC.soft }}>Exactly what the model receives — no raw database access.</div>
              </div>
              <Btn variant="outline" onClick={() => setPayload(null)} data-testid="mgmt-payload-close">Close</Btn>
            </div>
            <pre className="p-4 overflow-auto text-[11px] leading-relaxed" style={{ color: HC.text }}>
              {JSON.stringify(payload, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {drill && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setDrill(null)}>
          <div className="bg-white rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()} data-testid="mgmt-drilldown-modal">
            <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: HC.border }}>
              <div>
                <div className="font-brand font-bold text-lg" style={{ color: HC.text }}>Drill-down · {drill.key}</div>
                <div className="text-xs" style={{ color: HC.soft }}>{drill.source || "Loading…"}</div>
              </div>
              <Btn variant="outline" onClick={() => setDrill(null)} data-testid="mgmt-drilldown-close">Close</Btn>
            </div>
            <div className="p-4 overflow-auto">
              {drill.loading ? <Spinner /> : !drill.records?.length ? <Empty text="No supporting records" /> : (
                <table className="w-full text-[12px]">
                  <thead>
                    <tr>{Object.keys(drill.records[0]).slice(0, 8).map((c) => (
                      <th key={c} className="text-left text-[10px] font-bold uppercase tracking-wide pb-2 pr-3" style={{ color: HC.soft }}>{c.replace(/_/g, " ")}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {drill.records.slice(0, 100).map((r, i) => (
                      <tr key={i} className="border-t" style={{ borderColor: HC.borderSoft }}>
                        {Object.keys(drill.records[0]).slice(0, 8).map((c) => (
                          <td key={c} className="py-2 pr-3" style={{ color: HC.text }}>
                            {typeof r[c] === "object" ? JSON.stringify(r[c]) : String(r[c] ?? "—")}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
